/**
 * Admin Reseller RPC definitions.
 *
 * Admin endpoints for managing a reseller's Stripe Connect (Accounts v2)
 * connected account — create one connected account per reseller and read its
 * current id + status. All endpoints require admin role via `AdminMiddleware`.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Customer } from "@fm/db/models/Customer";
import { ResellerCommission, ResellerCommissionStatus, ResellerEarningsReport } from "@fm/db/models/ResellerCommission";
import { PayoutMethod, ResellerProfile, StripeAccountStatus } from "@fm/db/models/ResellerProfile";
import { RateTermsConfig, ResellerFeeType } from "@fm/db/models/ResellerRateTerms";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import {
  AgreementStorageError,
  AgreementStorageNotConfigured,
  ClerkAllowlistError,
  DatabaseError,
  EmailDeliveryError,
  Forbidden,
  RecordNotFoundError,
  SubscriptionError,
  Unauthorized,
} from "../errors.ts";
import { AdminResellerInviteStatus, ResellerReadiness } from "./reseller-derivations.ts";

// The admin read model's derived enums live in the pure derivations module so
// the handlers and the web seam share one definition. Re-export both (const AND
// type) so `./resellers.rpc.ts` remains the single import path for the RPC layer
// and the client barrel.
export { AdminResellerInviteStatus, ResellerReadiness } from "./reseller-derivations.ts";

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Selects a reseller by its customer id.
 * @since 1.0.0
 */
export const ResellerCustomerInput = Schema.Struct({
  resellerCustomerId: Schema.String,
});
export type ResellerCustomerInput = Schema.Schema.Type<typeof ResellerCustomerInput>;

/**
 * Request a hosted onboarding Account Link for a reseller's connected account.
 * `returnUrl` is where the reseller lands on completion; `refreshUrl` is where
 * Stripe redirects on an expired/used link (the caller re-mints there).
 * @since 1.0.0
 */
export const OnboardingLinkInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  returnUrl: Schema.String,
  refreshUrl: Schema.String,
});
export type OnboardingLinkInput = Schema.Schema.Type<typeof OnboardingLinkInput>;

/**
 * The section of the reseller's Stripe Dashboard a billing deep-link targets:
 * the billing overview, the invoices list, or the payments list.
 * @since 1.0.0
 */
export const ResellerBillingSection = Schema.Literal("billing", "invoices", "payments");
export type ResellerBillingSection = Schema.Schema.Type<typeof ResellerBillingSection>;

/**
 * Request a deep-link to a reseller's own Stripe Dashboard billing surface so
 * they can self-serve invoices/Payment Links for their own value-added services
 * (money flow 2 — direct charges on the reseller's connected account, no fee).
 * @since 1.0.0
 */
export const BillingDashboardLinkInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  section: ResellerBillingSection,
});
export type BillingDashboardLinkInput = Schema.Schema.Type<typeof BillingDashboardLinkInput>;

/**
 * Refund a clawinstall-product charge on a reseller's connected account.
 * `amount` (minor units) is omitted for a full refund. `reason` is the operator
 * note explaining the good-faith refund. `refundApplicationFee` defaults to
 * `true` — the good-faith policy returns our application fee so the reseller is
 * made whole; set it `false` (the CLI's `--keep-fee`) to refund the customer but
 * keep our fee. Disputes/chargebacks are NOT actioned here (Stripe + the
 * connected account handle them and we keep our fee).
 * @since 1.0.0
 */
export const RefundChargeInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  chargeId: Schema.String,
  amount: Schema.optional(Schema.Int),
  reason: Schema.String,
  refundApplicationFee: Schema.optional(Schema.Boolean),
});
export type RefundChargeInput = Schema.Schema.Type<typeof RefundChargeInput>;

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * The connected account id + derived status after a create/persist.
 * @since 1.0.0
 */
export const ConnectedAccountResult = Schema.Struct({
  stripeAccountId: Schema.String,
  stripeAccountStatus: StripeAccountStatus,
});
export type ConnectedAccountResult = Schema.Schema.Type<typeof ConnectedAccountResult>;

/**
 * The connected account id + status as currently persisted — both null when the
 * reseller has a profile but no connected account yet.
 * @since 1.0.0
 */
export const ConnectedAccountState = Schema.Struct({
  stripeAccountId: Schema.NullOr(Schema.String),
  stripeAccountStatus: Schema.NullOr(StripeAccountStatus),
});
export type ConnectedAccountState = Schema.Schema.Type<typeof ConnectedAccountState>;

/**
 * A freshly-minted hosted onboarding link plus its expiry (account links are
 * short-lived and single-use). `expiresAt` is a moment-in-time.
 * @since 1.0.0
 */
export const OnboardingLinkResult = Schema.Struct({
  url: Schema.String,
  expiresAt: Schema.DateTimeUtc,
});
export type OnboardingLinkResult = Schema.Schema.Type<typeof OnboardingLinkResult>;

/**
 * Live onboarding state for a reseller's connected account, as a dashboard
 * renders it: the derived status, the outstanding currently-due requirement
 * keys, and whether charges/payouts are enabled.
 * @since 1.0.0
 */
export const OnboardingStatusResult = Schema.Struct({
  stripeAccountStatus: StripeAccountStatus,
  requirementsDue: Schema.Array(Schema.String),
  chargesEnabled: Schema.Boolean,
  payoutsEnabled: Schema.Boolean,
});
export type OnboardingStatusResult = Schema.Schema.Type<typeof OnboardingStatusResult>;

/**
 * A deep-link to the reseller's own Stripe Dashboard billing surface.
 * @since 1.0.0
 */
export const BillingDashboardLinkResult = Schema.Struct({
  url: Schema.String,
});
export type BillingDashboardLinkResult = Schema.Schema.Type<typeof BillingDashboardLinkResult>;

/**
 * Summary of a completed refund. `amount` is the refunded amount in minor units;
 * `status` is Stripe's refund status (e.g. `succeeded`/`pending`).
 * `applicationFeeRefunded` records whether our application fee was returned to
 * the reseller (true for the default good-faith refund, false when the operator
 * kept our fee via `--keep-fee`).
 * @since 1.0.0
 */
export const RefundChargeResult = Schema.Struct({
  refundId: Schema.String,
  chargeId: Schema.String,
  amount: Schema.Int,
  status: Schema.String,
  applicationFeeRefunded: Schema.Boolean,
});
export type RefundChargeResult = Schema.Schema.Type<typeof RefundChargeResult>;

// ============================================================================
// Clawinstall product billing (CLA-520)
// ============================================================================

/**
 * Open the clawinstall-product subscription DIRECTLY on a reseller's connected
 * account so the CLA-488 application-fee + commission-ledger flow runs
 * automatically. `feeType` selects the reseller's rate terms and defaults to
 * `subscription`. `agentInstallationId` (optional) lets an agent-scoped rate
 * exception apply. The generated invoice carries reseller branding (direct
 * charge), and the per-invoice `application_fee_amount` is set later by the
 * merged `invoice.created` webhook — never here.
 * @since 1.0.0
 */
export const BillSubscribeInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  sourceCustomerId: Schema.String,
  priceId: Schema.String,
  signupPriceId: Schema.optional(Schema.String),
  feeType: Schema.optional(ResellerFeeType),
  agentInstallationId: Schema.optional(Schema.String),
});
export type BillSubscribeInput = Schema.Schema.Type<typeof BillSubscribeInput>;

/**
 * Find-or-create the clawinstall product + its prices ON a reseller's connected
 * account (direct-charge subscriptions must reference a price that lives on the
 * connected account — a platform price cannot be used). `monthlyAmount` is the
 * recurring subscription price and `signupAmount` the one-time signup fee, both
 * in minor units. Idempotent: re-running with the same amounts reuses the
 * existing product + prices rather than duplicating them, and persists the
 * resulting price ids on the reseller.
 * @since 1.0.0
 */
export const EnsurePricesInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  monthlyAmount: Schema.Int,
  signupAmount: Schema.Int,
});
export type EnsurePricesInput = Schema.Schema.Type<typeof EnsurePricesInput>;

/**
 * The clawinstall price ids provisioned on (or already present for) a reseller's
 * connected account: the recurring subscription price + the one-time signup
 * price.
 * @since 1.0.0
 */
export const EnsurePricesResult = Schema.Struct({
  subscriptionPriceId: Schema.String,
  signupPriceId: Schema.String,
});
export type EnsurePricesResult = Schema.Schema.Type<typeof EnsurePricesResult>;

/**
 * The reseller's currently-stored clawinstall price ids — both null when the
 * reseller has a profile but no prices have been provisioned yet. Surfaced by
 * `bill.price.show` and used by `bill subscribe` to default its `--price` /
 * `--signup-price` when omitted.
 * @since 1.0.0
 */
export const StripePricesState = Schema.Struct({
  subscriptionPriceId: Schema.NullOr(Schema.String),
  signupPriceId: Schema.NullOr(Schema.String),
});
export type StripePricesState = Schema.Schema.Type<typeof StripePricesState>;

/**
 * The opened subscription's id + Stripe status (e.g. `active`/`incomplete`),
 * plus the first invoice's `hosted_invoice_url` — the pay-up-front, due-on-receipt
 * link the reseller sends the customer. `hostedInvoiceUrl` is null when Stripe
 * surfaced no hosted URL for the first invoice.
 * @since 1.0.0
 */
export const BillSubscribeResult = Schema.Struct({
  subscriptionId: Schema.String,
  status: Schema.String,
  hostedInvoiceUrl: Schema.NullOr(Schema.String),
});
export type BillSubscribeResult = Schema.Schema.Type<typeof BillSubscribeResult>;

/**
 * One row of a reseller's clawinstall billing list: the connected-account
 * subscription id + status, the end-customer + fee type read off the metadata
 * contract, the price, and when it opened. `sourceCustomerId`/`feeType`/`priceId`
 * are null when the subscription predates the metadata contract.
 * @since 1.0.0
 */
export const BillSubscriptionSummary = Schema.Struct({
  subscriptionId: Schema.String,
  status: Schema.String,
  sourceCustomerId: Schema.NullOr(Schema.String),
  feeType: Schema.NullOr(Schema.String),
  priceId: Schema.NullOr(Schema.String),
  createdAt: Schema.DateTimeUtc,
});
export type BillSubscriptionSummary = Schema.Schema.Type<typeof BillSubscriptionSummary>;

// ============================================================================
// Lifecycle / rate-terms / attribution schemas (CLA-518)
// ============================================================================

/**
 * Selects the customer to promote to a reseller. Keyed on the plain customer id
 * (the reseller identity does not exist yet at create time).
 * @since 1.0.0
 */
export const ResellerCreateInput = Schema.Struct({
  customerId: Schema.NonEmptyTrimmedString,
});
export type ResellerCreateInput = Schema.Schema.Type<typeof ResellerCreateInput>;

/**
 * Patch a reseller's profile attributes. Only the supplied fields are written;
 * `null` clears the column. `payoutMethod` is the open-ended payout rail and
 * `agreementUrl` the signed-agreement link.
 * @since 1.0.0
 */
export const ResellerUpdateInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  agreementUrl: Schema.optional(Schema.NullOr(Schema.String)),
  payoutMethod: Schema.optional(Schema.NullOr(PayoutMethod)),
  // Per-reseller toggle for the (later) end-customer self-serve procurement
  // flow. Groundwork only — the flow it gates ships in a later ticket.
  customerSelfServeEnabled: Schema.optional(Schema.Boolean),
});
export type ResellerUpdateInput = Schema.Schema.Type<typeof ResellerUpdateInput>;

/**
 * Set the reseller's DEFAULT floor + upside-split terms for a fee type. Applies
 * to all the reseller's customers unless overridden by a more-specific exception.
 * @since 1.0.0
 */
export const RateSetDefaultInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  feeType: ResellerFeeType,
  floorAmount: Schema.Int,
  upsideShareBps: Schema.Int,
});
export type RateSetDefaultInput = Schema.Schema.Type<typeof RateSetDefaultInput>;

/**
 * Which exception bucket an override targets: a downstream `customer`
 * (`source_customer_id`) or a single `agent` (`agent_installation_id`).
 * @since 1.0.0
 */
export const RateExceptionScope = Schema.Literal("customer", "agent");
export type RateExceptionScope = Schema.Schema.Type<typeof RateExceptionScope>;

/**
 * Set an exception override for a single customer or agent, for one fee type.
 * @since 1.0.0
 */
export const RateSetExceptionInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  scope: RateExceptionScope,
  targetId: Schema.String,
  feeType: ResellerFeeType,
  floorAmount: Schema.Int,
  upsideShareBps: Schema.Int,
});
export type RateSetExceptionInput = Schema.Schema.Type<typeof RateSetExceptionInput>;

/**
 * Remove an exception override. With `feeType` omitted the whole target bucket
 * is dropped; with `feeType` set only that fee type is removed from the target.
 * @since 1.0.0
 */
export const RateRemoveExceptionInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  scope: RateExceptionScope,
  targetId: Schema.String,
  feeType: Schema.optional(ResellerFeeType),
});
export type RateRemoveExceptionInput = Schema.Schema.Type<typeof RateRemoveExceptionInput>;

/**
 * Pure preview of the floor + upside split a charge would produce under the
 * reseller's currently-resolved terms (no write). `sourceCustomerId` /
 * `agentInstallationId` select the most-specific exception, if any.
 * @since 1.0.0
 */
export const RateSimulateInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  feeType: ResellerFeeType,
  grossAmount: Schema.Int,
  sourceCustomerId: Schema.optional(Schema.String),
  agentInstallationId: Schema.optional(Schema.NullOr(Schema.String)),
});
export type RateSimulateInput = Schema.Schema.Type<typeof RateSimulateInput>;

/**
 * One row of the reseller list: the customer's account name + contact
 * name/email, the connected-account status (null when no account yet), the
 * attributed-customer count, plus the enriched admin read model — derived invite
 * status + expiry, active-agent count, month-to-date platform fees, and the
 * readiness breakdown (persisted fallback — the list makes no Stripe call).
 * @since 1.0.0
 */
export const ResellerSummary = Schema.Struct({
  customerId: Schema.String,
  name: Schema.String,
  email: Schema.NullOr(Schema.String),
  contactName: Schema.NullOr(Schema.String),
  stripeAccountStatus: Schema.NullOr(StripeAccountStatus),
  attributedCustomerCount: Schema.Int,
  inviteStatus: AdminResellerInviteStatus,
  inviteExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  activeAgentCount: Schema.Int,
  platformFeesMtdCents: Schema.Int,
  readiness: ResellerReadiness,
});
export type ResellerSummary = Schema.Schema.Type<typeof ResellerSummary>;

/**
 * The reseller's latest executed agreement, surfaced on the detail's agreement
 * panel: the accepted version, when it was accepted, and the typed signer name.
 * `null` on the detail when the reseller has never accepted. The presigned PDF
 * download is served separately by `admin.resellers.agreementDownloadUrl`.
 * @since 1.0.0
 */
export const ResellerAgreementSummary = Schema.Struct({
  version: Schema.String,
  acceptedAt: Schema.DateTimeUtc,
  signerName: Schema.String,
});
export type ResellerAgreementSummary = Schema.Schema.Type<typeof ResellerAgreementSummary>;

/**
 * Full reseller detail: the profile row, its connected-account status and
 * resolved rate terms surfaced for convenience, and the attributed-customer
 * count — plus the enriched admin read model: the reseller organization name,
 * contact name/email, derived invite status + expiry, readiness (persisted
 * fallback), active-agent count, month-to-date money, and the latest agreement.
 * @since 1.0.0
 */
export const ResellerDetail = Schema.Struct({
  profile: ResellerProfile.json,
  name: Schema.String,
  contactName: Schema.NullOr(Schema.String),
  contactEmail: Schema.NullOr(Schema.String),
  stripeAccountStatus: Schema.NullOr(StripeAccountStatus),
  rateTerms: Schema.NullOr(RateTermsConfig),
  attributedCustomerCount: Schema.Int,
  inviteStatus: AdminResellerInviteStatus,
  inviteExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  readiness: ResellerReadiness,
  /**
   * The reseller's "Active agents" overview stat: the number of the reseller's
   * customers' installs in status `active` (ACTIVE_INSTALL_STATUS). Installs in
   * `provisioning`, `pending_install`, `stopped`, `error` or a torn-down status
   * are NOT counted here — distinct from {@link ResellerSummary.activeAgentCount},
   * the list's not-torn-down "Agents" count.
   */
  activeAgentCount: Schema.Int,
  grossMtdCents: Schema.Int,
  platformFeesMtdCents: Schema.Int,
  agreement: Schema.NullOr(ResellerAgreementSummary),
});
export type ResellerDetail = Schema.Schema.Type<typeof ResellerDetail>;

/**
 * Result of `rates.simulate` — the application fee + reseller take and which
 * scope's terms produced them (`null` when the reseller has no terms for the
 * fee type, in which case the reseller keeps the whole gross).
 * @since 1.0.0
 */
export const SimulateRateResult = Schema.Struct({
  applicationFeeAmount: Schema.Int,
  resellerAmount: Schema.Int,
  appliedScope: Schema.NullOr(Schema.Literal("reseller", "customer", "agent")),
});
export type SimulateRateResult = Schema.Schema.Type<typeof SimulateRateResult>;

// ============================================================================
// Start-invite schema (CLA-588)
// ============================================================================

/**
 * Payload for `admin.resellers.invite`. `deliver` defaults to `true` — the
 * standard send / resend / regenerate-and-send flow that emails the tokenized
 * link. Pass `deliver: false` for the expired-invite "Generate new link" action:
 * the handler mints, allowlists and persists a fresh token but never emails, so
 * the operator copies the link out of the returned result.
 * @since 1.0.0
 */
export const ResellerInviteInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  deliver: Schema.optionalWith(Schema.Boolean, { default: () => true }),
});
export type ResellerInviteInput = Schema.Schema.Type<typeof ResellerInviteInput>;

/**
 * Result of `admin.resellers.invite` — the freshly-minted start link (so the CSR
 * can also copy-paste it), its expiry, and the address the invite was emailed to.
 * `link` carries the plaintext bearer token exactly once; only its hash is stored
 * at rest. `expiresAt` is a moment-in-time. `emailedTo` is `null` when the invite
 * was minted with `deliver: false` (rotated without an email).
 * @since 1.0.0
 */
export const ResellerInviteResult = Schema.Struct({
  link: Schema.String,
  expiresAt: Schema.DateTimeUtc,
  emailedTo: Schema.NullOr(Schema.String),
});
export type ResellerInviteResult = Schema.Schema.Type<typeof ResellerInviteResult>;

// ============================================================================
// Reporting schemas (CLA-522)
// ============================================================================

/**
 * Window for the per-reseller earnings report: a single reseller over an
 * inclusive `period_month` ('YYYY-MM') range. Delegated verbatim to the merged
 * `reseller-commissions` `earningsReport` accessor.
 * @since 1.0.0
 */
export const ResellerEarningsInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  periodMonthFrom: Schema.String,
  periodMonthTo: Schema.String,
});
export type ResellerEarningsInput = Schema.Schema.Type<typeof ResellerEarningsInput>;

/**
 * Select a reseller's raw commission-ledger rows, optionally narrowed to a
 * single `period_month` ('YYYY-MM') and/or a lifecycle `status`. Both filters
 * are applied in the handler over the reseller's full accrual list.
 * @since 1.0.0
 */
export const ResellerLedgerInput = Schema.Struct({
  resellerCustomerId: Schema.String,
  periodMonth: Schema.optional(Schema.String),
  status: Schema.optional(ResellerCommissionStatus),
});
export type ResellerLedgerInput = Schema.Schema.Type<typeof ResellerLedgerInput>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Reseller RPC group.
 *
 * @since 1.0.0
 */
export class AdminResellersRpcs extends RpcGroup.make(
  Rpc.make("admin.resellers.createConnectedAccount", {
    payload: ResellerCustomerInput,
    success: ConnectedAccountResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, SubscriptionError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.getConnectedAccount", {
    payload: ResellerCustomerInput,
    success: ConnectedAccountState,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.createOnboardingLink", {
    payload: OnboardingLinkInput,
    success: OnboardingLinkResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, SubscriptionError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.getOnboardingStatus", {
    payload: ResellerCustomerInput,
    success: OnboardingStatusResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, SubscriptionError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.getBillingDashboardLink", {
    payload: BillingDashboardLinkInput,
    success: BillingDashboardLinkResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  // Deep-link to the reseller's connected-account details page in the PLATFORM
  // Dashboard (what "Open Stripe dashboard" needs), NOT the reseller-facing
  // billing portal above. Resolves the brand Stripe client to decide the
  // test/live `/test` prefix, so a brand-resolution failure surfaces as
  // `SubscriptionError`.
  Rpc.make("admin.resellers.connectedAccountDashboardLink", {
    payload: ResellerCustomerInput,
    success: BillingDashboardLinkResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, SubscriptionError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.refundCharge", {
    payload: RefundChargeInput,
    success: RefundChargeResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, SubscriptionError),
  }).middleware(AdminMiddleware),
  // -- Clawinstall product billing (CLA-520) ---------------------------------
  Rpc.make("admin.resellers.bill.subscribe", {
    payload: BillSubscribeInput,
    success: BillSubscribeResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, SubscriptionError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.bill.list", {
    payload: ResellerCustomerInput,
    success: Schema.Array(BillSubscriptionSummary),
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, SubscriptionError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.bill.price.ensure", {
    payload: EnsurePricesInput,
    success: EnsurePricesResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, SubscriptionError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.bill.price.show", {
    payload: ResellerCustomerInput,
    success: StripePricesState,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  // -- Reseller lifecycle (CLA-518) ------------------------------------------
  Rpc.make("admin.resellers.create", {
    payload: ResellerCreateInput,
    success: ResellerSummary,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.list", {
    success: Schema.Array(ResellerSummary),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.get", {
    payload: ResellerCustomerInput,
    success: ResellerDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.update", {
    payload: ResellerUpdateInput,
    success: ResellerProfile.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.disable", {
    payload: ResellerCustomerInput,
    success: Schema.Void,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  // -- Start invite (CLA-588) ------------------------------------------------
  // Mint a hashed-at-rest start-invite token, email the tokenized start link to
  // the reseller's primary contact, and return the link + expiry so the CSR can
  // also copy-paste it. Re-invoking replaces the previous token (natural resend).
  // The invite is only persisted after the email provider accepts the send, so a
  // delivery failure leaves the prior invite untouched. Pass `deliver: false` to
  // rotate the link without emailing (the operator copies it out of the result).
  Rpc.make("admin.resellers.invite", {
    payload: ResellerInviteInput,
    success: ResellerInviteResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      EmailDeliveryError,
      ClerkAllowlistError,
    ),
  }).middleware(AdminMiddleware),
  // Revoke the reseller's outstanding start invite — clears the token hash +
  // sent/expiry/accepted columns so the invite reads `not_invited` again.
  Rpc.make("admin.resellers.invite.revoke", {
    payload: ResellerCustomerInput,
    success: Schema.Void,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  // Presign a short-lived download URL for the reseller's latest executed
  // agreement PDF. `RecordNotFoundError` when the reseller has never accepted.
  Rpc.make("admin.resellers.agreementDownloadUrl", {
    payload: ResellerCustomerInput,
    success: Schema.Struct({ url: Schema.String }),
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      AgreementStorageNotConfigured,
      AgreementStorageError,
    ),
  }).middleware(AdminMiddleware),
  // -- Rate terms (CLA-518) --------------------------------------------------
  Rpc.make("admin.resellers.rates.get", {
    payload: ResellerCustomerInput,
    success: Schema.NullOr(RateTermsConfig),
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.rates.setDefault", {
    payload: RateSetDefaultInput,
    success: RateTermsConfig,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.rates.setException", {
    payload: RateSetExceptionInput,
    success: RateTermsConfig,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.rates.removeException", {
    payload: RateRemoveExceptionInput,
    success: RateTermsConfig,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.rates.simulate", {
    payload: RateSimulateInput,
    success: SimulateRateResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  // -- Customer attribution (CLA-518) ----------------------------------------
  Rpc.make("admin.resellers.customers.list", {
    payload: ResellerCustomerInput,
    success: Schema.Array(Customer.json),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  // -- Reporting (CLA-522) ---------------------------------------------------
  Rpc.make("admin.resellers.earnings", {
    payload: ResellerEarningsInput,
    success: ResellerEarningsReport,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.resellers.ledger", {
    payload: ResellerLedgerInput,
    success: Schema.Array(ResellerCommission.json),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
